import { createSlice } from "@reduxjs/toolkit";

import {
  addKnowledgeBaseData,
  deleteKnowledgeBaseData,
  fetchKnowledgeBaseData,
  updateKnowledgeBaseData,
} from "../thunks/knowledgeBaseThunk";

import type { KnowledgeBaseItem } from "../../utils/types/knowledgeBase";

interface KnowledgeBaseState {
  knowledgeBaseList: KnowledgeBaseItem[];
  loading: boolean;
  error: string | null;
  isDataFetched?: boolean;
}

const initialState: KnowledgeBaseState = {
  knowledgeBaseList: [],
  loading: false,
  error: null,
  isDataFetched: false,
};

export const knowledgeBaseSlice = createSlice({
  name: "knowledgeBase",
  initialState,
  reducers: {
    resetDataFetched: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKnowledgeBaseData.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
        state.error = null;
      })
      .addCase(fetchKnowledgeBaseData.fulfilled, (state, action) => {
        state.knowledgeBaseList = action?.payload || [];
        state.loading = false;
      })
      .addCase(fetchKnowledgeBaseData.rejected, (state, action) => {
        state.error = action?.payload as string;
        state.isDataFetched = false;
        state.loading = false;
      });

    builder
      .addCase(updateKnowledgeBaseData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateKnowledgeBaseData.fulfilled, (state, action) => {
        state.loading = false;
        state.knowledgeBaseList = state.knowledgeBaseList.map(
          (item: KnowledgeBaseItem) => {
            if (item?.id === action?.payload?.data?.id) {
              return { ...item, ...action?.payload?.data };
            }
            return item;
          }
        );
      })
      .addCase(updateKnowledgeBaseData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(deleteKnowledgeBaseData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteKnowledgeBaseData.fulfilled, (state, action) => {
        state.loading = false;
        state.knowledgeBaseList = state.knowledgeBaseList.filter(
          (item) => item.id !== action?.payload?.id
        );
      })
      .addCase(deleteKnowledgeBaseData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(addKnowledgeBaseData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addKnowledgeBaseData.fulfilled, (state, action) => {
        state.loading = false;
        state.knowledgeBaseList = [action.payload, ...state.knowledgeBaseList];
      })
      .addCase(addKnowledgeBaseData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
  },
});

export const { resetDataFetched } = knowledgeBaseSlice.actions;
export default knowledgeBaseSlice.reducer;
